/**
 * audioQualityUtils — Per-chunk and aggregate audio quality assessment
 *
 * Phases 2 & 4 of audio-quality-gates.md: QC functions for rolling transcription
 * and post-recording aggregate assessment.
 *
 * See: docs/features/document-system/audio-quality-gates.md
 */
import { chunkQcVeryLowWpm, chunkQcLowWpm, minimumWordCount } from "../globals";

// ── QC result labels ───────────────────────────────────────
export const QC_NO_SPEECH = "no_speech";   // completely empty
export const QC_VERY_LOW = "very_low";     // < 10 wpm — near-nothing
export const QC_LOW = "low";               // < 30 wpm — sparse speech
export const QC_OK = "ok";                 // normal range

/**
 * Assess quality of a single transcribed chunk based on words-per-minute.
 *
 * @param {Object} params
 * @param {number} params.wordCount — Words returned by Deepgram for this chunk
 * @param {number} params.chunkDurationMs — Duration of the chunk in milliseconds
 * @returns {{ qcResult: string, wordsPerMinute: number }}
 */
export function assessChunkQuality({ wordCount, chunkDurationMs }) {
  const durationMinutes = chunkDurationMs / 60000;
  const wordsPerMinute = durationMinutes > 0 ? wordCount / durationMinutes : 0;

  let qcResult;
  if (wordCount === 0) {
    qcResult = QC_NO_SPEECH;
  } else if (wordsPerMinute < chunkQcVeryLowWpm) {
    qcResult = QC_VERY_LOW;
  } else if (wordsPerMinute < chunkQcLowWpm) {
    qcResult = QC_LOW;
  } else {
    qcResult = QC_OK;
  }

  return { qcResult, wordsPerMinute: Math.round(wordsPerMinute * 10) / 10 };
}

/**
 * Build a toast message for a chunk QC failure.
 *
 * @param {string} qcResult — One of QC_NO_SPEECH, QC_VERY_LOW, QC_LOW
 * @param {number} chunkIndex — 0-based chunk index
 * @returns {{ message: string, type: string } | null} — null if no warning needed
 */
export function getChunkQcWarning(qcResult, chunkIndex) {
  const isCanary = chunkIndex === 0;

  switch (qcResult) {
    case QC_NO_SPEECH:
      return {
        message: isCanary
          ? "No speech detected in the first minute of recording. Please check your microphone placement."
          : "No speech detected in recent audio. Check that your microphone is picking up sound.",
        type: "error",
      };

    case QC_VERY_LOW:
      return {
        message: isCanary
          ? "Very little speech detected in the first minute. If you're speaking, try moving your microphone closer."
          : "Very little speech detected in recent audio. Try moving your microphone closer.",
        type: "warning",
      };

    case QC_LOW:
      return {
        message: isCanary
          ? "Low speech detection so far. If you're speaking, try moving your microphone closer."
          : "Low speech detection in recent audio. Consider checking your microphone placement.",
        type: "warning",
      };

    case QC_OK:
    default:
      return null;
  }
}

/**
 * Build a full chunk QC result record for tracking.
 *
 * @param {Object} params
 * @param {number} params.chunkIndex
 * @param {number} params.chunkDurationMs
 * @param {number} params.wordCount
 * @param {Object} [params.quality] — Quality data from transcription response
 * @param {string} params.qcResult — From assessChunkQuality
 * @param {number} params.wordsPerMinute — From assessChunkQuality
 * @returns {Object} Chunk QC record
 */
export function buildChunkQcRecord({ chunkIndex, chunkDurationMs, wordCount, quality, qcResult, wordsPerMinute }) {
  return {
    chunkIndex,
    durationMs: chunkDurationMs,
    wordCount,
    wordsPerMinute,
    avgConfidence: quality?.avgConfidence ?? null,
    qcResult,
    transcribedAt: Date.now(),
  };
}

// ── Quality labels (Phase 4) ──────────────────────────────
export const QUALITY_EXCELLENT = "excellent";
export const QUALITY_GOOD = "good";
export const QUALITY_FAIR = "fair";
export const QUALITY_POOR = "poor";

/**
 * Aggregate overall quality from: transcript quality (Deepgram), audio metrics
 * (client-side volume monitoring), and per-chunk QC results.
 *
 * Returns a quality label + data summary suitable for QualityGateDialog and
 * for persisting to Firestore on the document record.
 *
 * @param {Object} params
 * @param {Object}   params.transcriptQuality — { avgConfidence, lowConfidenceSegments, lowConfidenceRatio } from backend
 * @param {number}   params.wordCount — Total word count from transcript
 * @param {number}   params.speakerCount — Detected speakers
 * @param {Object}   params.audioMetrics — From audioQualityMetricsRef: { totalDurationMs, silenceDurationMs, lowVolumeDurationMs, clippingDurationMs, warningsShown }
 * @param {Array}    params.chunkQcResults — Array of buildChunkQcRecord outputs
 * @returns {{ label: string, blocked: boolean, blockReason: string|null, summary: Object }}
 */
export function assessOverallQuality({ transcriptQuality, wordCount, speakerCount, audioMetrics, chunkQcResults }) {
  const avgConfidence = transcriptQuality?.avgConfidence ?? 1;
  const total = audioMetrics?.totalDurationMs || 1;
  const goodAudioRatio = Math.max(0, (total
    - (audioMetrics?.silenceDurationMs || 0)
    - (audioMetrics?.lowVolumeDurationMs || 0)
    - (audioMetrics?.clippingDurationMs || 0)
  ) / total);

  const validChunks = (chunkQcResults || []).filter(c => c && c.qcResult);
  const totalChunks = validChunks.length;
  const failedChunks = validChunks.filter(c => c.qcResult !== QC_OK).length;
  const noSpeechChunks = validChunks.filter(c => c.qcResult === QC_NO_SPEECH).length;
  const veryLowChunks = validChunks.filter(c => c.qcResult === QC_VERY_LOW).length;
  const lowChunks = validChunks.filter(c => c.qcResult === QC_LOW).length;
  const okChunks = validChunks.filter(c => c.qcResult === QC_OK).length;

  // Word count gate
  if (wordCount < minimumWordCount) {
    return {
      label: QUALITY_POOR,
      blocked: true,
      blockReason: `Transcript is too short (${wordCount} words). This may indicate a microphone problem or very brief recording.`,
      summary: buildQualitySummary({ avgConfidence, goodAudioRatio, totalChunks, failedChunks, noSpeechChunks, veryLowChunks, lowChunks, okChunks, wordCount, speakerCount, audioMetrics }),
    };
  }

  // Quality label derivation (per spec §3A, rebalanced)
  // Confidence is the primary signal — it's ground truth from Deepgram.
  // Volume metrics (goodAudioRatio) are secondary. Low-amplitude devices like
  // USB headsets and external interfaces produce low volume readings but high
  // transcript confidence. When confidence >= 0.85, volume concerns are overridden.
  const confidenceOverridesVolume = avgConfidence >= 0.85;

  // Weight later chunks more heavily — if the recording stabilized after early
  // issues (mic repositioned, etc.), early failures shouldn't drag the label down.
  // Compute a recency-weighted failure ratio: recent half counts 2x.
  let weightedFailureRatio = 0;
  if (totalChunks > 0) {
    const midpoint = Math.floor(totalChunks / 2);
    let weightedFailed = 0;
    let totalWeight = 0;
    validChunks.forEach((c, i) => {
      const weight = i >= midpoint ? 2 : 1;
      totalWeight += weight;
      if (c.qcResult !== QC_OK) weightedFailed += weight;
    });
    weightedFailureRatio = totalWeight > 0 ? weightedFailed / totalWeight : 0;
  }

  let label;
  if (avgConfidence >= 0.95 && (goodAudioRatio >= 0.90 || confidenceOverridesVolume) && failedChunks === 0) {
    label = QUALITY_EXCELLENT;
  } else if (avgConfidence >= 0.85 && (goodAudioRatio >= 0.75 || confidenceOverridesVolume) && failedChunks <= 1) {
    label = QUALITY_GOOD;
  } else if (avgConfidence < 0.70 || (goodAudioRatio < 0.50 && !confidenceOverridesVolume) || (totalChunks > 0 && weightedFailureRatio > 0.5)) {
    label = QUALITY_POOR;
  } else if (avgConfidence >= 0.70) {
    label = QUALITY_FAIR;
  } else {
    label = QUALITY_FAIR;
  }

  return {
    label,
    blocked: false,
    blockReason: null,
    summary: buildQualitySummary({ avgConfidence, goodAudioRatio, totalChunks, failedChunks, noSpeechChunks, veryLowChunks, lowChunks, okChunks, wordCount, speakerCount, audioMetrics }),
  };
}

/**
 * Build a quality summary object for Firestore persistence and dialog display.
 * @private
 */
function buildQualitySummary({ avgConfidence, goodAudioRatio, totalChunks, failedChunks, noSpeechChunks, veryLowChunks, lowChunks, okChunks, wordCount, speakerCount, audioMetrics }) {
  return {
    avgConfidence: Math.round(avgConfidence * 1000) / 1000,
    goodAudioRatio: Math.round(goodAudioRatio * 1000) / 1000,
    totalChunks,
    failedChunks,
    noSpeechChunks,
    veryLowChunks,
    lowChunks,
    okChunks,
    wordCount,
    speakerCount,
    silenceRatio: audioMetrics?.totalDurationMs ? Math.round((audioMetrics.silenceDurationMs / audioMetrics.totalDurationMs) * 1000) / 1000 : 0,
    lowVolumeRatio: audioMetrics?.totalDurationMs ? Math.round((audioMetrics.lowVolumeDurationMs / audioMetrics.totalDurationMs) * 1000) / 1000 : 0,
    clippingRatio: audioMetrics?.totalDurationMs ? Math.round((audioMetrics.clippingDurationMs / audioMetrics.totalDurationMs) * 1000) / 1000 : 0,
    volumeWarningsShown: audioMetrics?.warningsShown || 0,
  };
}

/**
 * Build the audioQualityNote string for display on the document banner.
 *
 * @param {string} label — Quality label (excellent/good/fair/poor)
 * @param {Object} summary — From assessOverallQuality().summary
 * @returns {string} Human-readable note
 */
export function buildQualityNote(label, summary) {
  if (label === QUALITY_EXCELLENT || label === QUALITY_GOOD) return "";

  const parts = [];
  if (summary.failedChunks > 0) {
    parts.push(`${summary.failedChunks} of ${summary.totalChunks} audio segments had low speech detection`);
  }
  if (summary.lowVolumeRatio > 0.10) {
    parts.push(`${Math.round(summary.lowVolumeRatio * 100)}% of recording had low audio volume`);
  }
  const confPct = Math.round(summary.avgConfidence * 100);
  parts.push(`Transcript confidence: ${confPct}%`);

  return `Audio quality issues detected during recording. ${parts.join(". ")}. Review for accuracy.`;
}

// ── Phase 6: Recording Status UI ──────────────────────────

/**
 * Status labels for the Recording Status Bar.
 * Maps QC results to user-friendly display strings.
 */
export const RECORDING_STATUS_LABELS = {
  ok: "Recording Quality OK",
  low: "Check Your Mic",
  very_low: "Not Detecting Speech",
  no_speech: "Not Detecting Speech",
  one_sided: "Only Detecting One Speaker",
  listening: "Listening...",
  waiting: "Waiting for Patient Approval...",
  analyzing: "Recording Started, Analyzing Quality...",
  silence: "Not Detecting Speech",
};

/**
 * Severity levels for status pill styling.
 */
export const RECORDING_STATUS_SEVERITY = {
  ok: "good",
  low: "warning",
  very_low: "error",
  no_speech: "error",
  one_sided: "warning",
  listening: "neutral",
  waiting: "neutral",
  analyzing: "neutral",
  silence: "warning",
};

/**
 * Check if recording appears to be one-sided (only one speaker detected).
 * Triggers when speakerCount === 1 for 2 or more consecutive chunks.
 * 
 * This catches cases where user is recording from a Chrome tab but only
 * their mic is being captured, not the remote participant.
 *
 * @param {Array} chunkResults — Array of chunk QC records with speakerCount
 * @param {number} [minConsecutive=2] — Minimum consecutive single-speaker chunks to trigger
 * @returns {boolean} True if one-sided recording detected
 */
export function checkOneSidedRecording(chunkResults, minConsecutive = 2) {
  if (!chunkResults || chunkResults.length < minConsecutive) {
    return false;
  }

  // Check last N chunks for consecutive single-speaker results
  const recentChunks = chunkResults.slice(-minConsecutive);
  return recentChunks.every(c => c && c.speakerCount === 1);
}

/**
 * Compute the current recording status based on chunk results.
 * Returns status label, severity, and icon for the status pill.
 *
 * @param {Array} chunkResults — Array of chunk QC records
 * @returns {{ label: string, severity: string, icon: string, qcResult: string } | null}
 */
export function computeRecordingStatus(chunkResults) {
  if (!chunkResults || chunkResults.length === 0) {
    return {
      label: RECORDING_STATUS_LABELS.listening,
      severity: "neutral",
      icon: "listening",
      qcResult: "listening",
    };
  }

  // Ignore synthetic silence timeline markers for status computation.
  // They are useful for timeline dots, but should not pin the status pill.
  const realChunkResults = chunkResults.filter(c => c && c.qcResult !== "silence");
  if (realChunkResults.length === 0) {
    return {
      label: RECORDING_STATUS_LABELS.listening,
      severity: "neutral",
      icon: "listening",
      qcResult: "listening",
    };
  }

  // Check for one-sided recording first (takes priority)
  if (checkOneSidedRecording(realChunkResults)) {
    return {
      label: RECORDING_STATUS_LABELS.one_sided,
      severity: RECORDING_STATUS_SEVERITY.one_sided,
      icon: "person",
      qcResult: "one_sided",
    };
  }

  // Otherwise, use the latest chunk's QC result
  const latestChunk = realChunkResults[realChunkResults.length - 1];
  const qcResult = latestChunk?.qcResult || "ok";

  return {
    label: RECORDING_STATUS_LABELS[qcResult] || RECORDING_STATUS_LABELS.ok,
    severity: RECORDING_STATUS_SEVERITY[qcResult] || "good",
    icon: qcResult === "ok" ? "checkmark" : qcResult === "low" ? "warning" : "mic-off",
    qcResult,
  };
}
